import {useCurrentRepository} from '@github-ui/current-repository'
import {CancelFeatureRequest, RequestFeature, useFeatureRequest} from '@github-ui/feature-request'
import {testIdProps} from '@github-ui/test-id-props'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {CopilotIcon} from '@primer/octicons-react'
import {AnchoredOverlay, Button, Link, LinkButton, Popover} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback, useState} from 'react'
import {useCurrentUser} from '@github-ui/current-user'
import type {CopilotInfo} from '@github-ui/copilot-common-types'
import type {FeatureRequestInfo} from '@github-ui/feature-request/types'
import styles from './CopilotPopover.module.css'

export const PopoverMessage = {
  ORG_ADMIN: `For an organization, developers writing less boilerplate code means more productivity, while learning
  new technologies means delivering better customers solutions. Try it in Codespaces or your file editor.`,
  ORG_MEMBER: `We noticed that you're personally paying for GitHub Copilot. Instead, ask your organization admin
  to purchase the business version of GitHub Copilot.`,
  STANDARD: `Spend less time creating boilerplate and repetitive code patterns, and more time building great software.
  Try it in Codespaces or your favorite file editor.`,
}

interface CopilotPopoverProps {
  view: 'blame' | 'edit' | 'preview'
  copilotInfo?: CopilotInfo
  className?: string
}

const FeatureRequest = ({
  featureRequestInfo,
  inProgress,
  toggleFeatureRequest,
  requested,
}: {
  featureRequestInfo: FeatureRequestInfo
  inProgress: boolean
  toggleFeatureRequest: () => void
  requested: boolean
}): React.ReactElement | null => {
  if (!featureRequestInfo?.showFeatureRequest) {
    return null
  }

  return requested ? (
    <CancelFeatureRequest inProgress={inProgress} toggleFeatureRequest={toggleFeatureRequest} />
  ) : (
    <RequestFeature
      inProgress={inProgress}
      toggleFeatureRequest={toggleFeatureRequest}
      featureName={featureRequestInfo?.featureName}
    />
  )
}

export const CopilotPopover = ({view, copilotInfo, className}: CopilotPopoverProps): React.ReactElement | null => {
  const {documentationUrl, notices, userAccess} = copilotInfo ?? {}
  const {
    business,
    orgHasCFBAccess,
    userHasCFIAccess,
    userHasOrgs,
    userIsOrgAdmin,
    userIsOrgMember,
    featureRequestInfo,
  } = userAccess ?? {}
  const {codeViewPopover: codeViewPopoverNotice} = notices ?? {}

  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const currentUser = useCurrentUser()
  const {isOrgOwned: repoIsOrgOwned, ownerLogin} = useCurrentRepository()
  const {inProgress, requested, dismissed, dismissedAt, toggleFeatureRequest} = useFeatureRequest(featureRequestInfo)

  const [isOpen, setIsOpen] = useState(false)
  const [popoverHidden, setPopoverHidden] = useState(false)
  const openPopover = useCallback(() => setIsOpen(true), [setIsOpen])
  const closePopover = useCallback(() => setIsOpen(false), [setIsOpen])

  const buttonText = useCallback((): string => {
    if (userAccess && repoIsOrgOwned) {
      if (userIsOrgMember && !userIsOrgAdmin && (!orgHasCFBAccess || userHasCFIAccess)) {
        return 'Your organization can pay for GitHub Copilot'
      }
    }

    return 'Code 55% faster with GitHub Copilot'
  }, [orgHasCFBAccess, repoIsOrgOwned, userAccess, userHasCFIAccess, userIsOrgAdmin, userIsOrgMember])

  const bodyText = (): string => {
    if (dismissed) {
      return ''
    }
    if (userAccess && repoIsOrgOwned) {
      if (userIsOrgAdmin) {
        return PopoverMessage.ORG_ADMIN
      } else if (userIsOrgMember && userHasCFIAccess) {
        return PopoverMessage.ORG_MEMBER
      }
    }
    return PopoverMessage.STANDARD
  }

  const dismissedMessage = (): React.ReactElement | null => {
    return (
      <>
        <p>Your request for Copilot Business was declined by an admin on {dismissedAt}.</p>
        <p className={'mb-0'}>
          <Link
            {...testIdProps('contact-admin-link')}
            aria-label={`Click this link to contact your admin.`}
            target="_blank"
            href={`/orgs/${ownerLogin}/people?query=role:owner`}
            onClick={() => sendAnalyticsClickLearnMore()}
          >
            Contact your admin
          </Link>
          {''} for more details around their decision.
        </p>
      </>
    )
  }

  const getUserRepoRelationship = (): string => {
    if (currentUser && ownerLogin === currentUser.login) {
      return 'owner'
    } else if (userIsOrgAdmin) {
      return 'admin'
    } else if (userIsOrgMember) {
      return 'member'
    } else {
      return 'personal'
    }
  }

  const sendAnalyticsClickOpenPopover = (): void => {
    if (currentUser) {
      sendClickAnalyticsEvent({
        category: 'copilot_popover_code_view',
        action: `click_to_open_popover_${view}`,
        label: `ref_cta:open_copilot_popover;owner:${ownerLogin};relationship:${getUserRepoRelationship()}`,
      })
    }
  }

  const sendAnalyticsClickPopoverCTA = (action: string, ctaText: string): void => {
    sendClickAnalyticsEvent({
      category: 'copilot_popover_code_view',
      action,
      label: `ref_cta:${ctaText};ref_loc:code_view_${view}`,
    })
  }

  const sendAnalyticsDismissPopover = (): void => {
    const refLoc = `${repoIsOrgOwned ? 'org_' : ''}code_view_${view}${userIsOrgAdmin ? '_org_admin' : ''}`

    sendClickAnalyticsEvent({
      category: 'copilot_popover_code_view',
      action: `click_to_dismiss_copilot_popover_forever`,
      label: `ref_cta:dont_show_again;ref_loc:${refLoc}`,
    })
  }

  const sendAnalyticsClickLearnMore = (): void => {
    const isCFBDocs = userAccess?.userHasOrgs ?? false

    sendClickAnalyticsEvent({
      category: 'copilot_popover_code_view',
      action: `click_to_go_to_copilot_for_${isCFBDocs ? 'business' : 'individuals'}_info`,
      label: 'ref_cta:learn_more;ref_loc:code_view',
    })
  }

  const bodyCTA = (): React.ReactElement | null => {
    const userCFBSeat = !!business
    const userHasAnyCopilotAccess = userCFBSeat || userHasCFIAccess

    if (!userHasAnyCopilotAccess && (!repoIsOrgOwned || (repoIsOrgOwned && !userIsOrgMember))) {
      return userHasOrgs ? (
        <LinkButton
          type="button"
          href="/settings/copilot"
          onClick={() => sendAnalyticsClickPopoverCTA('click_to_go_to_copilot_settings', 'get_github_copilot')}
        >
          Get GitHub Copilot
        </LinkButton>
      ) : (
        <LinkButton
          type="button"
          href="/github-copilot/signup"
          onClick={() => sendAnalyticsClickPopoverCTA('click_to_go_to_copilot_trial_signup', 'start_a_free_trial')}
        >
          Start a free trial
        </LinkButton>
      )
    }

    if (userIsOrgMember && !orgHasCFBAccess) {
      if (userIsOrgAdmin) {
        return (
          <LinkButton
            type="button"
            href={`/github-copilot/business_signup/organization/payment?org=${ownerLogin}`}
            onClick={() => sendAnalyticsClickPopoverCTA('click_to_buy_copilot_for_business', 'get_github_copilot')}
          >
            Get GitHub Copilot
          </LinkButton>
        )
      }
    }

    if (featureRequestInfo && !dismissed) {
      return (
        <FeatureRequest
          requested={requested}
          featureRequestInfo={featureRequestInfo}
          inProgress={inProgress}
          toggleFeatureRequest={toggleFeatureRequest}
        />
      )
    }

    return null
  }

  const onDismissPopover = (): void => {
    if (codeViewPopoverNotice) {
      verifiedFetch(codeViewPopoverNotice.dismissPath, {method: userIsOrgMember ? 'DELETE' : 'POST'})
      sendAnalyticsDismissPopover()
      setPopoverHidden(true)
    }
  }

  const learnMoreLink = (): React.ReactElement => {
    return (
      <Link
        {...testIdProps('copilot-popover-content-learn-more')}
        aria-label={`Click this link to learn more about copilot. This action opens in a new tab.`}
        target="_blank"
        href={documentationUrl}
        onClick={() => sendAnalyticsClickLearnMore()}
        className={styles.learnMoreLink}
      >
        Learn more
      </Link>
    )
  }

  const hideCopilotPopover = (): boolean => popoverHidden || !copilotInfo

  if (hideCopilotPopover()) {
    return null
  }
  return (
    <div className={className}>
      <AnchoredOverlay
        onOpen={openPopover}
        onClose={closePopover}
        open={isOpen}
        overlayProps={{
          role: 'dialog',
          style: {overflow: 'inherit'},
        }}
        focusZoneSettings={{disabled: true}}
        renderAnchor={anchorProps => (
          <Button
            {...anchorProps}
            {...testIdProps('copilot-popover-button')}
            leadingVisual={CopilotIcon}
            aria-label={buttonText()}
            onClick={() => {
              setIsOpen(!isOpen)
              sendAnalyticsClickOpenPopover()
            }}
            size="small"
            className={styles.copilotButton}
            variant="invisible"
          />
        )}
      >
        <Popover aria-label="GitHub Copilot" open={isOpen} {...testIdProps('copilot-popover-content')} caret="top">
          <Popover.Content className={styles.PopoverContent}>
            <h2 className={styles.popoverHeading}>Code 55% faster with GitHub Copilot</h2>
            <div className={styles.popoverBodyText}>
              <span {...testIdProps('copilot-popover-body-text')}>{bodyText()}</span>
              {dismissed ? dismissedMessage() : learnMoreLink()}
            </div>
            <div className={styles.popoverActionsContainer}>
              {bodyCTA()}
              <Link
                {...testIdProps('copilot-popover-dismiss-button')}
                onClick={onDismissPopover}
                className={clsx(dismissed ? styles.dismissLinkNoMargin : styles.dismissLink)}
              >
                Don&apos;t show again
              </Link>
            </div>
          </Popover.Content>
        </Popover>
      </AnchoredOverlay>
    </div>
  )
}
